import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { Firestore, doc, getDoc, updateDoc, collection, query, where, getDocs, limit, Timestamp } from '@angular/fire/firestore';
import {
  getDownloadURL,
  ref,
  Storage,
  uploadBytesResumable,
} from '@angular/fire/storage';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import moment from 'moment';
import { ToastrService } from 'ngx-toastr';
import { InitialsPipe } from '../../../../pipes/initials.pipe';
import { UtilsService } from '../../../../services/utils.service';
import { BtnBackComponent } from '../../../components/buttons/btn-back/btn-back.component';
import { BtnSubmitComponent } from '../../../components/buttons/btn-submit/btn-submit.component';
import { GenderComponent } from '../../../components/dropdowns/gender/gender.component';
import { CheckboxComponent } from '../../../components/inputs/checkbox/checkbox.component';

@Component({
  selector: 'app-edit-child-profile',
  standalone: true,
  imports: [
    CommonModule,
    BtnSubmitComponent,
    BtnBackComponent,
    ReactiveFormsModule,
    GenderComponent,
    InitialsPipe,
    CheckboxComponent,
  ],
  templateUrl: './edit-child-profile.component.html',
  styleUrl: './edit-child-profile.component.css',
})
export class EditChildProfileComponent implements OnInit {
  private readonly storage: Storage = inject(Storage);
  private readonly afs: Firestore = inject(Firestore);
  private readonly route: ActivatedRoute = inject(ActivatedRoute);
  private readonly router: Router = inject(Router);
  private readonly toastr: ToastrService = inject(ToastrService);
  private fb: FormBuilder = inject(FormBuilder);

  groupId: string | null = null;
  studentId: string | null = null;
  studentDocId: string | null = null;
  student: any | null = null;

  today: Date = new Date();
  pageForm: FormGroup;
  progress: string | null = null;
  working: boolean = false;
  utils = UtilsService;

  constructor() {
    this.pageForm = this.fb.group({
      name: new FormControl('', [Validators.required, Validators.minLength(2)]),
      dob: new FormControl(),
      gender: new FormControl('unspecified'),
      showDob: new FormControl(true),
    });
  }

  ngOnInit() {
    this.groupId = this.route.snapshot.paramMap.get('id');
    this.studentId = this.route.snapshot.paramMap.get('studentId');

    if (this.groupId && this.studentId) {
      this.working = true;
      const membershipsRef = collection(this.afs, 'memberships');
      const q = query(
        membershipsRef,
        where('groupId', '==', this.groupId),
        where('memberId', '==', this.studentId),
        limit(1)
      );

      getDocs(q).then(snap => {
        this.working = false;
        if (!snap.empty) {
          const docSnap = snap.docs[0];
          this.studentDocId = docSnap.id;
          this.student = docSnap.data();
          this.pageForm.patchValue({
            name: this.student.name,
            dob: this.student.dob ? moment(this.student.dob.toDate()).format('YYYY-MM-DD') : null,
            gender: this.student.gender ?? 'unspecified',
            showDob: this.student.showDob !== false
          });
        } else {
          this.toastr.error('Student membership record not found');
        }
      }).catch(err => {
        this.working = false;
        console.error('Error fetching student details:', err);
        this.toastr.error('Error fetching student details');
      });
    }
  }

  uploadFile(event: any) {
    if (!event.target.files) return;

    const file: File = event.target.files[0];
    if (file == null || !this.groupId || !this.studentId || !this.studentDocId) return;

    const fileExtn = file.name.split('.').pop();
    const storageRef = ref(
      this.storage,
      `unregistered/${this.groupId}/${this.studentId}.${fileExtn}`
    );

    const metadata = {
      contentType: file.type
    };

    const uploadTask = uploadBytesResumable(storageRef, file, metadata);

    uploadTask.on(
      'state_changed',
      (snapshot) => {
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        this.progress = `Upload: ${progress.toFixed(2)}%`;
      },
      (error) => {
        this.toastr.error(
          `Error occurred while uploading the profile image. Please try again later.\n${error}`,
          'Error Uploading Image'
        );
        this.progress = null;
      },
      () => {
        this.toastr.success(
          'Profile image uploaded successfully.',
          'Success'
        );
        getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
          if (this.student) {
            this.student.photoURL = downloadURL;
          }
          // Save photoURL in the membership document directly using the correct document ID
          if (this.studentDocId) {
            const studentDocRef = doc(this.afs, 'memberships', this.studentDocId);
            updateDoc(studentDocRef, { photoURL: downloadURL }).catch(err => {
              console.error('Failed to update photoURL in membership:', err);
            });
          }
        });
        this.progress = null;
      }
    );
  }

  update() {
    if (this.pageForm.invalid || !this.groupId || !this.studentId || !this.studentDocId) return;

    this.working = true;
    const name = this.pageForm.get('name')?.value;
    const dobVal = this.pageForm.get('dob')?.value;
    const dob = dobVal ? moment(dobVal).toDate() : null;
    const gender = this.pageForm.get('gender')?.value ?? 'unspecified';
    const showDob = this.pageForm.get('showDob')?.value;

    const studentDocRef = doc(this.afs, 'memberships', this.studentDocId);
    
    const updateData: any = {
      name,
      dob,
      gender,
      showDob,
      modified: new Date()
    };

    updateDoc(studentDocRef, updateData).then(() => {
      this.working = false;
      this.toastr.success('Student profile updated successfully');

      if (this.student) {
        this.student.name = name;
        this.student.dob = dob ? Timestamp.fromDate(dob) : null;
        this.student.gender = gender;
        this.student.showDob = showDob;
      }
      


      this.router.navigate(['..'], { relativeTo: this.route });
    }).catch(err => {
      this.working = false;
      this.toastr.error('Failed to update student profile');
    });
  }

  deleteProfileImage() {
    if (!this.groupId || !this.studentId || !this.studentDocId) return;
    this.working = true;
    const studentDocRef = doc(this.afs, 'memberships', this.studentDocId);
    
    updateDoc(studentDocRef, { photoURL: null }).then(() => {
      this.working = false;
      if (this.student) {
        this.student.photoURL = null;
      }
      this.toastr.success('Profile image deleted successfully');
    }).catch(err => {
      this.working = false;
      this.toastr.error('Failed to delete profile image');
    });
  }
}
